// The account — the storefront band's entry into everything that is the
// customer's own at this restaurant: who they are, where they get food
// delivered, the loyalty card, the store's offers, and the orders. Each
// row reads its own door; a missing profile only greys what needs one.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { fetchMyCard, fetchPublicCoupons, fetchStore, money, type LoyaltyCard, type PublicCoupon, type StorePublic } from '../../lib/api';
import { AccountHead, accountPath, useMe } from './shared';

export function Account() {
  const { rid, sid } = useParams() as { rid: string; sid: string };
  const { me, error } = useMe(rid);
  const [store, setStore] = useState<StorePublic | null>(null);
  const [card, setCard] = useState<LoyaltyCard | null | undefined>(undefined);
  const [offers, setOffers] = useState<PublicCoupon[] | null>(null);

  useEffect(() => {
    fetchStore(rid, sid).then((s) => { setStore(s); document.title = `Your account — ${s.name}`; }, () => setStore(null));
    fetchMyCard(rid).then(setCard, () => setCard(null));
    fetchPublicCoupons(rid, sid).then(setOffers, () => setOffers([]));
  }, [rid, sid]);

  const row = (page: string, title: string, detail: string, attrs: Record<string, string | number> = {}) => (
    <Link to={page.startsWith('/') ? page : accountPath(rid, sid, page)} className="co-sec acct-row" {...attrs}>
      <span className="acct-row-tx">
        <span className="acct-row-tt">{title}</span>
        <span className="acct-row-dt">{detail}</span>
      </span>
      <span className="acct-row-go" aria-hidden>›</span>
    </Link>
  );

  return (
    <div className="mo-page" data-section="account">
      <AccountHead back={`/r/${rid}/s/${sid}`} backLabel="Back to the menu" kicker={store?.name} title="Your account" />
      {error && <div className="co-err acct-err" role="alert">{error}</div>}
      {me === undefined && !error && <div className="loading">Looking…</div>}
      {me !== undefined && (
        <>
          {row('profile', me ? me.name : 'Set up your profile', me ? [me.phoneE164, me.email].filter(Boolean).join(' · ') : 'Your name and mobile — the store knows you by your number', { 'data-row': 'profile' })}
          {row(`/r/${rid}/s/${sid}/orders`, 'My orders', 'Every order here, live status and receipts', { 'data-row': 'orders' })}
          {row(`/r/${rid}/s/${sid}/bookings`, 'My bookings', 'Tables booked at this store', { 'data-row': 'bookings' })}
          {row('addresses', 'Saved addresses', me ? (me.addresses.length ? `${me.addresses.length} saved · delivery picks them up` : 'None yet') : 'Set up your profile first', { 'data-row': 'addresses', 'data-count': me?.addresses.length ?? 0 })}
          {row('loyalty', 'Loyalty card', card === undefined ? '…' : card ? `${card.rewards.length} of ${card.ordersPerCard} orders · ${money(card.valueInCents)} banked${card.isRedeemable ? ' · ready to redeem' : ''}` : 'No card yet', { 'data-row': 'loyalty' })}
          {row('offers', 'Offers', offers === null ? '…' : offers.length ? `${offers.length} on now` : 'Nothing on right now', { 'data-row': 'offers', 'data-count': offers?.length ?? 0 })}
        </>
      )}
    </div>
  );
}
